import React, { useState } from 'react';
import { X, Save, Wrench, ListChecks, Image as ImageIcon, Clock, Car, AlertTriangle, History, ShieldCheck, Plus, Trash2, Copy, Check, Upload, Link2 } from 'lucide-react';
import { useData } from '../context/DataContext';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';

const CHECK_STATUS = ['OK', 'Aandacht', 'Vervangen', 'N.v.t.'];
const fileToB64 = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
const fmtTijd = (s) => { try { return new Date(s).toLocaleString('nl-NL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return s; } };

const TABS = [
  ['notities', 'Notities', Wrench], ['checklist', 'Checklist', ListChecks], ['fotos', "Foto's", ImageIcon],
  ['uren', 'Uren', Clock], ['proefrit', 'Proefrit', Car], ['schade', 'Schade-inname', AlertTriangle],
  ['tijdlijn', 'Tijdlijn', History], ['akkoord', 'Akkoord', ShieldCheck],
];

export default function WerkorderModal({ bon, onClose }) {
  const { t } = useT();
  const { updateBon } = useData();
  const [tab, setTab] = useState('notities');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [d, setD] = useState(() => ({
    monteurNotities: bon.monteurNotities || '',
    checklist: bon.checklist || [],
    fotosVoor: bon.fotosVoor || [], fotosNa: bon.fotosNa || [],
    urenregistratie: bon.urenregistratie || [],
    proefrit: bon.proefrit || { gereden: false, km: '', notitie: '' },
    schadeInname: bon.schadeInname || { punten: [], notitie: '' },
    tijdlijn: bon.tijdlijn || [],
    akkoord: bon.akkoord || null,
  }));
  const u = (k, v) => setD(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await updateBon(bon.id, { ...bon, ...d });
      toast.success(t('Werkorder opgeslagen', 'Werkorder opgeslagen'));
      onClose();
    } catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const addTijdlijn = (gebeurtenis, type = 'handmatig') => u('tijdlijn', [...d.tijdlijn, { tijd: new Date().toISOString(), type, gebeurtenis }]);

  const onPhotos = async (e, key) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - d[key].length);
    const b64s = await Promise.all(files.map(fileToB64));
    u(key, [...d[key], ...b64s]); e.target.value = '';
  };

  const genLink = async () => {
    try {
      const res = await carsysApi.createAkkoordLink(bon.id);
      const akk = { ...(d.akkoord || {}), token: res.token, status: res.status };
      u('akkoord', akk);
      toast.success(t('Akkoord-link aangemaakt', 'Akkoord-link aangemaakt'));
    } catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
  };
  const publicUrl = d.akkoord?.token ? `${window.location.origin}/akkoord/${d.akkoord.token}` : '';

  const urenTotaal = d.urenregistratie.reduce((s, r) => s + (parseInt(r.minuten) || 0), 0);

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720, width: '94vw' }} data-testid="werkorder-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}><Wrench size={17} style={{ verticalAlign: -3, marginRight: 5 }} />{t('Digitale werkorder', 'Digitale werkorder')} · {bon.nummer}</h3>
          <button className="cs-btn ghost" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e2e8f0', marginBottom: 14, overflowX: 'auto' }}>
          {TABS.map(([id, lbl, Icon]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`wo-tab-${id}`}
              style={{ padding: '7px 10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', color: tab === id ? '#2563eb' : '#64748b', borderBottom: tab === id ? '2px solid #2563eb' : '2px solid transparent' }}>
              <Icon size={13} style={{ verticalAlign: -2, marginRight: 4 }} />{t(lbl, lbl)}
            </button>
          ))}
        </div>

        <div style={{ minHeight: 240, maxHeight: '56vh', overflowY: 'auto', paddingRight: 4 }}>
          {tab === 'notities' && (
            <div data-testid="wo-notities">
              <label className="cs-section-title">{t('Monteursnotities (intern)', 'Monteursnotities (intern)')}</label>
              <textarea className="cs-input" rows={8} value={d.monteurNotities} onChange={e => u('monteurNotities', e.target.value)} data-testid="wo-notities-text" placeholder={t('Bevindingen, gebruikte onderdelen, bijzonderheden…', 'Bevindingen, gebruikte onderdelen, bijzonderheden…')} />
            </div>
          )}

          {tab === 'checklist' && (
            <div data-testid="wo-checklist">
              <button className="cs-btn" onClick={() => u('checklist', [...d.checklist, { punt: '', status: 'OK', notitie: '' }])} data-testid="wo-check-add"><Plus size={14} /> {t('Controlepunt', 'Controlepunt')}</button>
              <div className="space-y-2" style={{ marginTop: 10 }}>
                {d.checklist.map((c, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 32px', gap: 6, alignItems: 'center' }} data-testid={`wo-check-${i}`}>
                    <input className="cs-input" placeholder={t('Controlepunt', 'Controlepunt')} value={c.punt} onChange={e => u('checklist', d.checklist.map((x, idx) => idx === i ? { ...x, punt: e.target.value } : x))} />
                    <select className="cs-input" value={c.status} onChange={e => u('checklist', d.checklist.map((x, idx) => idx === i ? { ...x, status: e.target.value } : x))}
                      style={{ fontWeight: 700, color: c.status === 'Vervangen' ? '#b91c1c' : c.status === 'Aandacht' ? '#b45309' : c.status === 'OK' ? '#15803d' : '#64748b' }}>
                      {CHECK_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button className="cs-btn ghost" onClick={() => u('checklist', d.checklist.filter((_, idx) => idx !== i))}><Trash2 size={14} color="#dc2626" /></button>
                  </div>
                ))}
                {d.checklist.length === 0 && <div className="cs-empty">{t('Nog geen controlepunten', 'Nog geen controlepunten')}</div>}
              </div>
            </div>
          )}

          {tab === 'fotos' && (
            <div data-testid="wo-fotos" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[['fotosVoor', 'Vóór reparatie'], ['fotosNa', 'Na reparatie']].map(([key, lbl]) => (
                <div key={key}>
                  <label className="cs-section-title">{t(lbl, lbl)} ({d[key].length}/6)</label>
                  <input id={`wo-${key}`} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => onPhotos(e, key)} data-testid={`wo-${key}-input`} />
                  <button className="cs-btn" style={{ width: '100%', justifyContent: 'center', border: '2px dashed #cbd5e1', marginBottom: 8 }} disabled={d[key].length >= 6} onClick={() => document.getElementById(`wo-${key}`).click()} data-testid={`wo-${key}-btn`}><Upload size={14} /> {t('Upload', 'Upload')}</button>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {d[key].map((src, i) => (
                      <div key={i} style={{ position: 'relative', aspectRatio: '4/3', borderRadius: 8, overflow: 'hidden', background: '#e2e8f0' }}>
                        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button onClick={() => u(key, d[key].filter((_, idx) => idx !== i))} style={{ position: 'absolute', top: 3, right: 3, width: 22, height: 22, border: 'none', borderRadius: 6, background: 'rgba(220,38,38,.9)', color: '#fff', cursor: 'pointer' }}><Trash2 size={12} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'uren' && (
            <div data-testid="wo-uren">
              <button className="cs-btn" onClick={() => u('urenregistratie', [...d.urenregistratie, { monteur: '', omschrijving: '', minuten: 30, datum: new Date().toISOString().slice(0, 10) }])} data-testid="wo-uren-add"><Plus size={14} /> {t('Urenregel', 'Urenregel')}</button>
              <div className="space-y-2" style={{ marginTop: 10 }}>
                {d.urenregistratie.map((r, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr 90px 32px', gap: 6, alignItems: 'center' }} data-testid={`wo-uren-${i}`}>
                    <input className="cs-input" placeholder={t('Monteur')} value={r.monteur} onChange={e => u('urenregistratie', d.urenregistratie.map((x, idx) => idx === i ? { ...x, monteur: e.target.value } : x))} />
                    <input className="cs-input" placeholder={t('Werkzaamheid', 'Werkzaamheid')} value={r.omschrijving} onChange={e => u('urenregistratie', d.urenregistratie.map((x, idx) => idx === i ? { ...x, omschrijving: e.target.value } : x))} />
                    <input className="cs-input" type="number" placeholder="min" value={r.minuten} onChange={e => u('urenregistratie', d.urenregistratie.map((x, idx) => idx === i ? { ...x, minuten: e.target.value } : x))} data-testid={`wo-uren-min-${i}`} />
                    <button className="cs-btn ghost" onClick={() => u('urenregistratie', d.urenregistratie.filter((_, idx) => idx !== i))}><Trash2 size={14} color="#dc2626" /></button>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, textAlign: 'right', fontWeight: 700 }} data-testid="wo-uren-totaal">{t('Totaal', 'Totaal')}: {Math.floor(urenTotaal / 60)}u {urenTotaal % 60}m</div>
            </div>
          )}

          {tab === 'proefrit' && (
            <div data-testid="wo-proefrit">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, marginBottom: 10 }}>
                <input type="checkbox" checked={d.proefrit.gereden} onChange={e => u('proefrit', { ...d.proefrit, gereden: e.target.checked })} data-testid="wo-proefrit-gereden" /> {t('Proefrit gemaakt', 'Proefrit gemaakt')}
              </label>
              <label className="cs-section-title">{t('Km bij proefrit', 'Km bij proefrit')}</label>
              <input className="cs-input" type="number" value={d.proefrit.km} onChange={e => u('proefrit', { ...d.proefrit, km: e.target.value })} style={{ marginBottom: 10 }} />
              <label className="cs-section-title">{t('Bevindingen proefrit', 'Bevindingen proefrit')}</label>
              <textarea className="cs-input" rows={5} value={d.proefrit.notitie} onChange={e => u('proefrit', { ...d.proefrit, notitie: e.target.value })} data-testid="wo-proefrit-notitie" placeholder={t('Geluiden, trillingen, remweg, sturen…', 'Geluiden, trillingen, remweg, sturen…')} />
            </div>
          )}

          {tab === 'schade' && (
            <div data-testid="wo-schade">
              <button className="cs-btn" onClick={() => u('schadeInname', { ...d.schadeInname, punten: [...d.schadeInname.punten, { locatie: '', type: '', omschrijving: '' }] })} data-testid="wo-schade-add"><Plus size={14} /> {t('Schadepunt', 'Schadepunt')}</button>
              <div className="space-y-2" style={{ marginTop: 10 }}>
                {d.schadeInname.punten.map((p, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 32px', gap: 6 }} data-testid={`wo-schade-${i}`}>
                    <input className="cs-input" placeholder={t('Locatie (bv. voorbumper L)', 'Locatie (bv. voorbumper L)')} value={p.locatie} onChange={e => u('schadeInname', { ...d.schadeInname, punten: d.schadeInname.punten.map((x, idx) => idx === i ? { ...x, locatie: e.target.value } : x) })} />
                    <input className="cs-input" placeholder={t('Type (kras/deuk/roest)', 'Type (kras/deuk/roest)')} value={p.type} onChange={e => u('schadeInname', { ...d.schadeInname, punten: d.schadeInname.punten.map((x, idx) => idx === i ? { ...x, type: e.target.value } : x) })} />
                    <button className="cs-btn ghost" onClick={() => u('schadeInname', { ...d.schadeInname, punten: d.schadeInname.punten.filter((_, idx) => idx !== i) })}><Trash2 size={14} color="#dc2626" /></button>
                  </div>
                ))}
              </div>
              <label className="cs-section-title" style={{ marginTop: 12 }}>{t('Algemene opmerkingen inname', 'Algemene opmerkingen inname')}</label>
              <textarea className="cs-input" rows={3} value={d.schadeInname.notitie} onChange={e => u('schadeInname', { ...d.schadeInname, notitie: e.target.value })} />
            </div>
          )}

          {tab === 'tijdlijn' && (
            <div data-testid="wo-tijdlijn">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {['Auto gebracht', 'Intake gedaan', 'Diagnose klaar', 'Reparatie gestart', 'Reparatie gereed', 'Klant opgehaald'].map(ev => (
                  <button key={ev} className="cs-btn ghost" style={{ fontSize: 12 }} onClick={() => addTijdlijn(ev, 'status')} data-testid={`wo-tl-${ev.split(' ')[0]}`}><Plus size={12} /> {t(ev, ev)}</button>
                ))}
              </div>
              <div style={{ position: 'relative', paddingLeft: 16, borderLeft: '2px solid #e2e8f0' }}>
                {[...d.tijdlijn].sort((a, b) => (a.tijd || '').localeCompare(b.tijd || '')).map((e, i) => (
                  <div key={i} style={{ position: 'relative', marginBottom: 12 }} data-testid={`wo-tl-item-${i}`}>
                    <span style={{ position: 'absolute', left: -22, top: 3, width: 9, height: 9, borderRadius: '50%', background: e.type === 'akkoord' ? '#16a34a' : '#2563eb' }} />
                    <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{fmtTijd(e.tijd)}</div>
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{e.gebeurtenis}</div>
                  </div>
                ))}
                {d.tijdlijn.length === 0 && <div className="cs-empty">{t('Nog geen gebeurtenissen', 'Nog geen gebeurtenissen')}</div>}
              </div>
            </div>
          )}

          {tab === 'akkoord' && (
            <div data-testid="wo-akkoord">
              {d.akkoord?.status === 'akkoord' ? (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 16 }} data-testid="wo-akkoord-done">
                  <div style={{ fontWeight: 700, color: '#15803d', display: 'flex', alignItems: 'center', gap: 6 }}><Check size={16} /> {t('Klant akkoord', 'Klant akkoord')}</div>
                  <div style={{ fontSize: 13, color: '#475569', marginTop: 6 }}>{t('Door')}: <b>{d.akkoord.naam}</b> · {fmtTijd(d.akkoord.datum)}</div>
                  <div style={{ fontSize: 11.5, color: '#94a3b8' }}>IP {d.akkoord.ip || '—'} · {(d.akkoord.browser || '').slice(0, 60)}</div>
                  {d.akkoord.handtekening && <img src={d.akkoord.handtekening} alt="handtekening" style={{ marginTop: 8, maxWidth: 240, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }} data-testid="wo-akkoord-sig" />}
                </div>
              ) : (
                <>
                  <p style={{ fontSize: 13.5, color: '#475569', marginTop: 0 }}>{t('Maak een publieke link aan zodat de klant op zijn telefoon de werkzaamheden kan goedkeuren en digitaal ondertekenen.', 'Maak een publieke link aan zodat de klant op zijn telefoon de werkzaamheden kan goedkeuren en digitaal ondertekenen.')}</p>
                  {!publicUrl ? (
                    <button className="cs-btn primary" onClick={genLink} data-testid="wo-genlink"><Link2 size={14} /> {t('Akkoord-link aanmaken', 'Akkoord-link aanmaken')}</button>
                  ) : (
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
                      <label className="cs-section-title">{t('Publieke akkoord-link', 'Publieke akkoord-link')}</label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input className="cs-input" readOnly value={publicUrl} data-testid="wo-akkoord-url" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }} />
                        <button className="cs-btn" onClick={() => { navigator.clipboard?.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }} data-testid="wo-akkoord-copy">{copied ? <Check size={14} /> : <Copy size={14} />}</button>
                      </div>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>{t('Status', 'Status')}: {d.akkoord?.status === 'afgewezen' ? t('Afgewezen door klant', 'Afgewezen door klant') : t('Wacht op akkoord', 'Wacht op akkoord')}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
          <button className="cs-btn" onClick={onClose}>{t('Sluiten', 'Sluiten')}</button>
          <button className="cs-btn primary" onClick={save} disabled={saving} data-testid="wo-save"><Save size={14} /> {t('Opslaan')}</button>
        </div>
      </div>
    </div>
  );
}
